import { CdkDrag, type DragConstrainPosition } from '@angular/cdk/drag-drop';
import { Overlay, type OverlayRef, ViewportRuler } from '@angular/cdk/overlay';
import type { OnInit } from '@angular/core';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type Observable, of } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import {
  type FeatureTooltipDirectiveConfig,
  FeatureTooltipWrapperDirective,
} from '@core/product-updates-legacy';
import { TellentBrand } from '@core/tellent-types';
import { OVERLAY_LEVEL, WindowRef } from '@core/user-interface-legacy';
import {
  ComponentPositionPair,
  ComponentTooltipPosition,
  ObserveOnElement,
} from '@core/user-interface-types';
import {
  EvaluationPanelPositionService,
  EvaluationPanelStateService,
  EvaluationPanelTooltipService,
} from '@recruitee/evaluations-logic';
import type {
  EvaluationPanelCandidate,
  EvaluationPanelData,
  EvaluationPanelMentionUsers,
  EvaluationPanelOffer,
  QuickEvaluationFormValue,
  TemplateEvaluationFormValue,
} from '@recruitee/evaluations-types';
import {
  alignToProfileRight,
  constrainToViewport,
  distanceFromProfileRight,
  getProfileElement,
} from '@recruitee/evaluations-util';

import { EvaluationFloatingPanelComponent } from '../../evaluation-floating-panel/evaluation-floating-panel.component';

const PROFILE_OBSERVE_RETRIES = 60;

@Component({
  selector: 'rt-evaluation-panel-container',
  templateUrl: './evaluation-panel-container.component.html',
  styleUrls: ['./evaluation-panel-container.component.less'],
  providers: [
    EvaluationPanelStateService,
    {
      provide: OVERLAY_LEVEL,
      useFactory: () => {
        const parent = inject(OVERLAY_LEVEL, { optional: true, skipSelf: true });
        return parent !== null ? parent + 1 : 0;
      },
    },
  ],
  imports: [CdkDrag, FeatureTooltipWrapperDirective, EvaluationFloatingPanelComponent],
})
export class EvaluationPanelContainerComponent implements OnInit {
  private readonly overlay = inject(Overlay);
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly windowRef = inject(WindowRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly i18n = inject(I18nService);
  private readonly positionService = inject(EvaluationPanelPositionService);
  private readonly tooltipService = inject(EvaluationPanelTooltipService);
  private readonly cdkDrag = viewChild(CdkDrag);
  private readonly floatingPanel = viewChild(EvaluationFloatingPanelComponent);

  private readonly tooltipOriginRef = computed(
    () => new ElementRef(<Node>this.overlayRef()?.overlayElement),
  );
  private readonly positionSettled = signal(false);

  protected readonly panelState = inject(EvaluationPanelStateService);

  protected readonly tooltipConfig: FeatureTooltipDirectiveConfig = {
    id: 'evaluation_panel_tooltip',
    flagName: 'evaluationPanelTooltipUsed',
    isNew: true,
    brand: TellentBrand.Tellent,
    title: this.i18n.translate('app.evaluation_panel.tooltip.title'),
    description: this.i18n.translate('app.evaluation_panel.tooltip.description'),
    ctaText: this.i18n.translate('app.common.got_it'),
    withPush: false,
    observeOnElement: ObserveOnElement.Custom,
    observeOnCustomElement: this.tooltipOriginRef,
    positions: {
      [ComponentTooltipPosition.Left]: new ComponentPositionPair(
        { originX: 'start', originY: 'top' },
        { overlayX: 'end', overlayY: 'top' },
        'topright',
        -24,
        -8,
      ),
    },
  };

  protected readonly tooltipEnabled = computed(
    () =>
      this.tooltipService.used() === false &&
      !!this.overlayRef() &&
      this.positionSettled() &&
      this.panelState.isExpanded() &&
      !this.panelState.isFullScreen(),
  );

  public readonly data = input.required<EvaluationPanelData>();
  public readonly candidate = input<EvaluationPanelCandidate | null>(null);
  public readonly availableOffers = input<EvaluationPanelOffer[]>([]);
  public readonly mentionUsers = input<EvaluationPanelMentionUsers | null>(null);
  public readonly boundary = input<HTMLElement>();
  public readonly overlayRef = input<OverlayRef>();
  public readonly minimized = input(false);
  public readonly restore = input<QuickEvaluationFormValue | TemplateEvaluationFormValue | null>(
    null,
  );
  public readonly close = output<void>();

  private profileObserver?: ResizeObserver;
  private _presentationMode = effect(() => this.applyPresentationMode());
  private _observeProfile = effect(() => this.observeProfile(this.boundary()));

  constructor() {
    this.viewportRuler
      .change(100)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.repositionToProfile());

    this.destroyRef.onDestroy(() => this.profileObserver?.disconnect());
  }

  public ngOnInit(): void {
    if (this.minimized()) {
      this.panelState.isExpanded.set(false);
    }
  }

  public handleBackdropClick(): void {
    if (this.panelState.isFullScreen()) {
      this.panelState.toggleFullScreen();
    }
  }

  public confirmReplace(): Observable<boolean> {
    return this.floatingPanel()?.confirmReplace() ?? of(true);
  }

  protected readonly constrainPosition: DragConstrainPosition = (
    point,
    _dragRef,
    dimensions,
    pickup,
  ) => constrainToViewport(point, dimensions, pickup, this.windowRef.nativeWindow);

  protected onDragEnded(): void {
    const overlayRef = this.overlayRef();
    if (!overlayRef) {
      return;
    }

    const distance = distanceFromProfileRight(this.boundary(), overlayRef.overlayElement);
    if (distance !== null) {
      this.positionService.saveDistanceFromRight(distance);
    }
  }

  private applyPresentationMode(): void {
    const overlayRef = this.overlayRef();
    if (!overlayRef) {
      return;
    }

    const backdrop = overlayRef.backdropElement;

    if (this.panelState.isFullScreen()) {
      overlayRef.updatePositionStrategy(
        this.overlay.position().global().centerHorizontally().centerVertically(),
      );
      this.cdkDrag()?.reset();

      if (backdrop) {
        backdrop.style.backgroundColor = 'rgba(36, 38, 46, 0.7)';
        backdrop.style.pointerEvents = 'auto';
      }
    } else {
      afterNextRender(() => this.applyFloatingPosition(overlayRef), { injector: this.injector });

      if (backdrop) {
        backdrop.style.backgroundColor = 'transparent';
        backdrop.style.pointerEvents = 'none';
      }
    }
  }

  private applyFloatingPosition(overlayRef: OverlayRef): void {
    const savedDistance = this.positionService.getDistanceFromRight();
    const panelWidth = overlayRef.overlayElement.getBoundingClientRect().width;

    this.cdkDrag()?.reset();

    alignToProfileRight({
      boundary: this.boundary(),
      nativeWindow: this.windowRef.nativeWindow,
      panelWidth,
      apply: offset => {
        overlayRef.updatePositionStrategy(
          this.overlay.position().global().right(`${offset}px`).bottom('0'),
        );
        this.positionSettled.set(true);
      },
      distanceFromRight: savedDistance ?? undefined,
    });
  }

  private observeProfile(
    boundary: HTMLElement | undefined,
    retries = PROFILE_OBSERVE_RETRIES,
  ): void {
    if (!boundary) {
      return;
    }

    const profile = getProfileElement(boundary);
    if (!profile) {
      if (retries > 0) {
        this.windowRef.nativeWindow.requestAnimationFrame(() =>
          this.observeProfile(boundary, retries - 1),
        );
      }

      return;
    }

    this.profileObserver?.disconnect();
    this.profileObserver = new ResizeObserver(() => this.repositionToProfile());
    this.profileObserver.observe(profile);
  }

  private repositionToProfile(): void {
    const overlayRef = this.overlayRef();
    if (overlayRef && !this.panelState.isFullScreen()) {
      this.applyFloatingPosition(overlayRef);
    }
  }
}
